<!-- eslint-disable max-len -->
<template>
  <v-card :loading="isLoading">
    <v-card-title>
      <span class="text-lg-h5">
        Configuration for
        <br>
        <span class="font-weight-bold ml-1">{{ formTitle }}</span>
      </span>
    </v-card-title>

    <template v-if="!isLoading">
      <v-card-text>
        <v-form>
          <v-list>
            <v-list-item>
              <v-text-field
                v-model.number="formData.range"
                type="number"
                label="Range"
                hint="Number of days back to look for Recency Prefilter."
                persistent-hint
                dense
                :disabled="isLocked"
                :error="v$.formData.range.$error"
                :error-messages="getErrorMessage(v$.formData.range)"
              />
            </v-list-item>

            <!-- CCR Score with two inputs -->
            <ClarityRiskExposedConfigItem
              v-model="formData.ccr_score.log_only"
              title="CCR Score"
              :is-locked="isLocked"
            >
              <v-text-field
                v-model.number="formData.ccr_score.min_value"
                type="number"
                label="CCR Low"
                hint="Minimum vaule required to pass."
                persistent-hint
                dense
                :disabled="isLocked"
                :error="v$.formData.ccr_score.min_value.$error"
                :error-messages="getErrorMessage(v$.formData.ccr_score.min_value)"
              />

              <v-text-field
                v-model.number="formData.ccr_score.max_value"
                type="number"
                label="CCR High"
                hint="Minimum vaule required to pass."
                persistent-hint
                dense
                :disabled="isLocked"
                :error="v$.formData.ccr_score.max_value.$error"
                :error-messages="getErrorMessage(v$.formData.ccr_score.max_value)"
              />

              <v-select
                v-model="formData.ccr_score.score_version"
                :items="[1, 2]"
                label="Score Version"
                dense
                :disabled="isLocked"
                :error="v$.formData.ccr_score.score_version.$error"
                :error-messages="getErrorMessage(v$.formData.ccr_score.score_version)"
              />
            </ClarityRiskExposedConfigItem>

            <!-- Number of Loans (Current & Open) -->
            <ClarityRiskExposedConfigItem
              v-model="formData.number_of_loans_current_and_open.log_only"
              title="Number of Loans (Current & Open)"
              :is-locked="isLocked"
            >
              <v-text-field
                v-model.number="formData.number_of_loans_current_and_open.max_value"
                type="number"
                label="Max Value"
                hint="Maximum vaule required to pass."
                persistent-hint
                dense
                :disabled="isLocked"
                :error="v$.formData.number_of_loans_current_and_open.max_value.$error"
                :error-messages="getErrorMessage(v$.formData.number_of_loans_current_and_open.max_value)"
              />
            </ClarityRiskExposedConfigItem>

            <!-- Number of Loans Past Due -->
            <ClarityRiskExposedConfigItem
              v-model="formData.number_of_loans_past_due.log_only"
              title="Number of Loans Past Due"
              :is-locked="isLocked"
            >
              <v-text-field
                v-model.number="formData.number_of_loans_past_due.max_value"
                type="number"
                label="Max Value"
                hint="Maximum vaule required to pass."
                persistent-hint
                dense
                :disabled="isLocked"
                :error="v$.formData.number_of_loans_past_due.max_value.$error"
                :error-messages="getErrorMessage(v$.formData.number_of_loans_past_due.max_value)"
              />
            </ClarityRiskExposedConfigItem>

            <!-- Days Since Last Loan Charged Off -->
            <ClarityRiskExposedConfigItem
              v-model="formData.days_since_last_loan_charged_off.log_only"
              title="Days Since Last Loan Charged Off"
              :is-locked="isLocked"
            >
              <v-text-field
                v-model.number="formData.days_since_last_loan_charged_off.min_days"
                type="number"
                label="Min Days"
                hint="Minimum vaule required to pass."
                persistent-hint
                dense
                :disabled="isLocked"
                :error="v$.formData.days_since_last_loan_charged_off.min_days.$error"
                :error-messages="getErrorMessage(v$.formData.days_since_last_loan_charged_off.min_days)"
              />
            </ClarityRiskExposedConfigItem>

            <!-- Worst Payment Rating -->
            <ClarityRiskExposedConfigItem
              v-model="formData.worst_payment_rating.log_only"
              title="Worst Payment Rating"
              :is-locked="isLocked"
            >
              <v-text-field
                v-model.trim="formData.worst_payment_rating.allowed_values"
                label="Allowed Values"
                hint="Value(s) required to pass."
                persistent-hint
                dense
                :disabled="isLocked"
                :error="v$.formData.worst_payment_rating.allowed_values.$error"
                :error-messages="getErrorMessage(v$.formData.worst_payment_rating.allowed_values)"
              />
            </ClarityRiskExposedConfigItem>

            <!-- Number of Bank Accounts -->
            <ClarityRiskExposedConfigItem
              v-model="formData.number_of_bank_accounts.log_only"
              title="Number of Bank Accounts"
              :is-locked="isLocked"
            >
              <v-text-field
                v-model.number="formData.number_of_bank_accounts.max_value"
                type="number"
                label="Max Value"
                hint="Maximum vaule required to pass."
                persistent-hint
                dense
                :disabled="isLocked"
                :error="v$.formData.number_of_bank_accounts.max_value.$error"
                :error-messages="getErrorMessage(v$.formData.number_of_bank_accounts.max_value)"
              />
            </ClarityRiskExposedConfigItem>
          </v-list>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          color="blue darken-1"
          text
          @click="handleCancel"
        >
          Cancel
        </v-btn>
        <v-btn
          color="blue darken-1"
          text
          :disabled="isLocked"
          @click="handleSubmit"
        >
          Save
        </v-btn>
      </v-card-actions>
    </template>
  </v-card>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { helpers, requiredIf, required } from '@vuelidate/validators';
import ClarityRiskExposedConfigItem from './ClarityRiskExposedConfigItem.vue';

/**
 * Comma-separated tokens, no spaces (Vuelidate 2: helpers.regex only accepts RegExp args).
 */
const commaSeparatedRegex = helpers.regex(/^([^,\s]+(,[^,\s]+)*)$/);

export default {
  name: 'ClarityRiskExposedConfig',
  components: {
    ClarityRiskExposedConfigItem,
  },
  props: {
    customVars: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    formTitle: {
      type: String,
      default: '',
    },
    isLocked: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['cancel', 'submit'],
  setup() { return { v$: useVuelidate() }; },
  data() {
    return {
      formData: {
        range: null,
        ccr_score: {
          min_value: null,
          max_value: null,
          score_version: null,
          log_only: false,
        },
        number_of_loans_current_and_open: {
          max_value: null,
          log_only: false,
        },
        number_of_loans_past_due: {
          max_value: null,
          log_only: false,
        },
        days_since_last_loan_charged_off: {
          min_days: null,
          log_only: false,
        },
        worst_payment_rating: {
          allowed_values: '',
          log_only: false,
        },
        number_of_bank_accounts: {
          max_value: null,
          log_only: false,
        },
      },
    };
  },
  validations() {
    return {
      formData: {
        range: { required },
        ccr_score: {
          min_value: {
            required: requiredIf(() => !this.formData.ccr_score.log_only),
          },
          max_value: {
            required: requiredIf(() => !this.formData.ccr_score.log_only),
          },
          score_version: {
            required: requiredIf(() => !this.formData.ccr_score.log_only),
          },
        },
        number_of_loans_current_and_open: {
          max_value: {
            required: requiredIf(() => !this.formData.number_of_loans_current_and_open.log_only),
          },
        },
        number_of_loans_past_due: {
          max_value: {
            required: requiredIf(() => !this.formData.number_of_loans_past_due.log_only),
          },
        },
        days_since_last_loan_charged_off: {
          min_days: {
            required: requiredIf(() => !this.formData.days_since_last_loan_charged_off.log_only),
          },
        },
        worst_payment_rating: {
          allowed_values: {
            required: requiredIf(() => !this.formData.worst_payment_rating.log_only),
            commaSeparated(value) {
              if (this.formData.worst_payment_rating.log_only) return true;
              return commaSeparatedRegex(value);
            },
          },
        },
        number_of_bank_accounts: {
          max_value: {
            required: requiredIf(() => !this.formData.number_of_bank_accounts.log_only),
          },
        },
      },
    };
  },
  watch: {
    /**
     * Update formData when customVars changes.
     */
    customVars({ payload: { value: currentData } }) {
      if (currentData) {
        // format the worst_payment_rating.allowed_values as a string
        const payloadData = {
          ...currentData,
          worst_payment_rating: {
            ...currentData.worst_payment_rating,
            allowed_values: currentData.worst_payment_rating.allowed_values?.join(','),
          },
        };
        Object.assign(this.formData, payloadData);
      }
    },
  },
  methods: {
    handleCancel() {
      this.$emit('cancel');
    },
    handleSubmit() {
      this.v$.$touch();
      if (!this.v$.$invalid) {
        // format the worst_payment_rating.allowed_values as an array
        const payload = {
          ...this.formData,
          worst_payment_rating: {
            ...this.formData.worst_payment_rating,
            allowed_values: this.formData.worst_payment_rating.allowed_values?.split(','),
          },
        };
        this.$emit('submit', { payload });
      }
    },
    getErrorMessage(validation) {
      if (!validation.$dirty) return '';
      if (validation.required?.$invalid) return 'This field is required';
      if (validation.commaSeparated?.$invalid) {
        return 'Enter a valid comma-separated list (no spaces)';
      }
      return '';
    },
  },
};
</script>
